import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenToSquare,
  faPlus,
  faTimes,
  faBarsFilter,
  faCalendar,
  faCircleQuestion,
} from "@fortawesome/pro-regular-svg-icons";
import useOnclickOutside from "hooks/useOnclickOutside/useOnclickOutside";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { showError, showSuccess } from "redux/actions/ui";

import Page from "components/page/Page";
import Card from "components/card/Card";
import DataTable from "components/dataTable/DataTable";
import SelectNew, { TSelectOption } from "components/select/SelectNew";
import Drawer from "components/drawerSlide/drawer";
import { Chip } from "components/Chip/Chip";
import {
  GetCheckIns,
  GetPricingSheets,
  PutCheckIns,
  IPutCheckIns,
  PutCopyCheckIns,
} from "api/rpc/2024-04/facilityAdmin/teesheet/checkIn";
import { StatusCode } from "api/protocols";
import { groupBy, isEqualWith, isNull } from "lodash";
import { convertTime, convertHoles, errorMessage } from "helpers/Helpers";
import { Select } from "components/select/index";
import Callout from "components/callout/Callout";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import Checkbox from "components/form/checkbox/Checkbox";
import Tabs from "components/tabs/Tabs";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { GetCustomerType } from "api/rpc/2024-04/facilityAdmin/customer/type";
import { ICourse, TCustomerType } from "redux/reducers/models/facility";
import "./teeSheetCheckIn.scss";
import Input from "components/form/input/Input";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import { ButtonNew as Button } from "components/buttonNew";
import useModal from "hooks/modals/useModal";
import useDebounce from "hooks/events/useDebounce";
import Portal from "elements/Portal";
import { LocaleCurrency } from "helpers/Locale";
import FormLayout from "components/form/FormLayout";
import {
  getStoredApplication,
  SESSION_FILTER_APPLICATION_KEY,
  SESSION_FILTER_DAY_KEY,
} from "../pricingSheets/pricingSheetApplications";

interface ICheckIn {
  id: number;
  customerTypeId: number;
  pricingSheetId: number;
  variantId: number;
  customer_type: ICustomerType;
  customerType: string;
  pricingSheet: string;
  pricingSheetCourse: string;
  pricingSheetApplication: string;
  holes: number;
  dayOfWeekNumber: number;
  courseId: number;
  teeSheetPriceOverride: string;
  onlinePriceOverride: string;
  prepaidPriceOverrideFixed: string;
  prepaidPriceOverridePercent: string;
  pricingSheetCourseId: number;
  pricingSheetStartDate?: string;
  pricingSheetEndDate?: string;
  variantPrice?: number;
}

interface IPricingSheetOption {
  id: number;
  title: string;
  startDate?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  application: string;
  dayOfWeekNumber: number;
}

interface IPricingSheetApiVariant {
  id: number;
  title: string;
  product?: {
    id: number;
    title: string;
  };
}

interface IPricingSheetApiResponse {
  id: number;
  title: string;
  start_date?: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  application: string;
  day_of_week_number: number;
  variants?: IPricingSheetApiVariant[];
}

function normalizePricingSheetApiResponse(value: unknown): IPricingSheetApiResponse | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }

  const sheet = value as Record<string, unknown>;
  const id = typeof sheet.id === "number" ? sheet.id : Number(sheet.id);
  const dayOfWeekNumber =
    typeof sheet.day_of_week_number === "number" ? sheet.day_of_week_number : Number(sheet.day_of_week_number);

  if (
    !Number.isFinite(id) ||
    typeof sheet.title !== "string" ||
    typeof sheet.application !== "string" ||
    !Number.isFinite(dayOfWeekNumber)
  ) {
    return null;
  }

  return {
    id,
    title: sheet.title,
    start_date: typeof sheet.start_date === "string" ? sheet.start_date : undefined,
    end_date: typeof sheet.end_date === "string" ? sheet.end_date : undefined,
    start_time: typeof sheet.start_time === "string" ? sheet.start_time : undefined,
    end_time: typeof sheet.end_time === "string" ? sheet.end_time : undefined,
    application: sheet.application,
    day_of_week_number: dayOfWeekNumber,
    variants: Array.isArray(sheet.variants) ? (sheet.variants as IPricingSheetApiVariant[]) : undefined,
  };
}

function parsePricingSheetsResponse(data: unknown): IPricingSheetApiResponse[] {
  if (!Array.isArray(data)) {
    return [];
  }

  return data
    .map(normalizePricingSheetApiResponse)
    .filter((sheet): sheet is IPricingSheetApiResponse => sheet !== null);
}

function mapPricingSheetFromApi(pricingSheet: IPricingSheetApiResponse): IPricingSheetOption {
  return {
    id: pricingSheet.id,
    title: pricingSheet.title,
    startDate: pricingSheet.start_date,
    endDate: pricingSheet.end_date,
    startTime: pricingSheet.start_time,
    endTime: pricingSheet.end_time,
    application: pricingSheet.application,
    dayOfWeekNumber: pricingSheet.day_of_week_number,
  };
}

function mapPricingSheetVariantFromApi(variant: IPricingSheetApiVariant): IVariant {
  return {
    id: variant.id,
    type: variant.title,
    productId: variant.product?.id,
    productTitle: variant.product?.title,
  };
}

type TPricingSheetSelectExtra = {
  title: string;
  supportText: string;
};

interface IDuplicateState {
  selectedCourseId: number;
  showModal: boolean;
  selectedDays: DayValue[];
  otherDaysCheckIns: Partial<Record<DayValue, ICheckIn[]>>;
  loading: boolean;
  warningDismissed: boolean;
}

interface IFilterDrawerState {
  customerTypeIds: number[];
  holes: number[];
  courses: number[];
  emptyVariant: number;
  customerTypeSearch: string;
}

type ActiveFilterTag =
  | { key: string; type: "customerType"; customerTypeId: number; label: string }
  | { key: string; type: "holes"; holes: number; label: string }
  | { key: string; type: "course"; courseId: number; label: string }
  | { key: string; type: "emptyVariant"; label: string };

interface ICustomerType {
  id: number;
  title: string;
  subtitle: string;
  full_title: string;
  application: string;
  booking_category: { id: number; client_id: number; title: string };
  client_id: number;
  default: number;
  color: string;
  default_booking_category_id: number;
}

interface IVariant {
  id: number;
  type: string;
  productId: number;
  productTitle: string;
}

interface ICheckInOption {
  productSheetId: number;
  variants: IVariant[];
}

interface IFilterState {
  application: Array<string>;
  emptyVariant: number;
  filterInProgress: boolean;
  holes: Array<number> | null;
  courses: Array<number> | null;
  customerTypes: Array<TCustomerType>;
  customerTypeSearch: string;
  drawerCustomerTypeIds: number[] | null;
}

enum Day {
  Holiday = 99,
  Monday = 1,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
  Sunday,
}

type DayValue = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 99;

const DAYS_OF_WEEK = {
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
  7: "Sunday",
  99: "Holiday",
};

export const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
export const weekdend = ["Saturday", "Sunday", "Holiday"];

const CHECK_IN_APPLICATION_VALUES = [
  "green_fee",
  "power_cart",
  "no_show",
  "green_fee_league",
  "power_cart_league",
] as const;

const COPY_TARGET_DAYS: DayValue[] = [
  Day.Monday,
  Day.Tuesday,
  Day.Wednesday,
  Day.Thursday,
  Day.Friday,
  Day.Saturday,
  Day.Sunday,
  Day.Holiday,
];

const DAY_ABBREV: Record<DayValue, string> = {
  1: "Mon",
  2: "Tue",
  3: "Wed",
  4: "Thu",
  5: "Fri",
  6: "Sat",
  7: "Sun",
  99: "Holiday",
};

const FILTER_CUSTOMER_TYPE_LIMIT = 10;

export default function TeeSheetCheckIn() {
  const dispatch = useAppDispatch();
  const { facilityStore } = useAppSelector(store => store);
  const { getCurrencySymbol } = useCurrencyFacility();
  const [checkInState, setCheckInState] = useState<ICheckIn[]>([]);
  const [checkInStateBeforeChanges, setCheckInStateBeforeChanges] = useState<ICheckIn[]>([]);
  const [duplicateState, setDuplicateState] = useState<IDuplicateState>({
    selectedCourseId: null,
    showModal: false,
    selectedDays: [],
    otherDaysCheckIns: {},
    loading: false,
    warningDismissed: false,
  });
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [filterDrawerBuffer, setFilterDrawerBuffer] = useState<IFilterDrawerState>({
    customerTypeIds: [],
    holes: [],
    courses: [],
    emptyVariant: 0,
    customerTypeSearch: "",
  });
  const [showAllDrawerCustomerTypes, setShowAllDrawerCustomerTypes] = useState(false);

  const { Option } = Select;

  const [checkInOptionState, setCheckInOptionState] = useState<ICheckInOption[]>([]);
  const [pricingSheets, setPricingSheets] = useState<IPricingSheetOption[]>([]);
  const [selectedPricingSheetId, setSelectedPricingSheetId] = useState<number | null>(null);

  const [filterDayState, setFilterDayState] = useState<DayValue>(Day.Monday);
  const [checkInsLoaded, setCheckInsLoaded] = useState(false);

  const [checkInIds, setCheckInIds] = useState<Array<number>>([]);
  const [variantSearches, setVariantSearches] = useState<Record<number, string>>({});
  const [variantFocused, setVariantFocused] = useState<Record<number, boolean>>({});

  const [filterState, setFilterState] = useState<IFilterState>({
    application: [getStoredApplication()],
    emptyVariant: 0,
    holes: null,
    courses: null,
    filterInProgress: false,
    customerTypes: [],
    customerTypeSearch: "",
    drawerCustomerTypeIds: null,
  });

  const {
    state: priceOverrideModal,
    updateModal: updatePriceOverrideModal,
    closeModal: closeUpdateOverrideModal,
  } = useModal<{
    type: "tee_sheet" | "online" | "prepaid";
    price: string;
    currentCheckIn: ICheckIn;
    checkInIndex: number;
    prepaidType: "fixed" | "percent";
  }>({ type: "tee_sheet", price: "", checkInIndex: null, currentCheckIn: null, prepaidType: "fixed" });
  const [openOverrideMenuCheckInId, setOpenOverrideMenuCheckInId] = useState<number | null>(null);
  const overrideTriggerRef = useRef<HTMLDivElement>(null);
  const overrideMenuPanelRef = useRef<HTMLDivElement>(null);
  const [overrideMenuPosition, setOverrideMenuPosition] = useState<{ top: number; left: number } | null>(null);

  useOnclickOutside(() => setOpenOverrideMenuCheckInId(null), {
    refs: [overrideTriggerRef, overrideMenuPanelRef],
    disabled: openOverrideMenuCheckInId === null,
  });

  const { t } = useTranslation();
  const history = useHistory();
  const currencyCode = facilityStore?.facility?.default_currency_code ?? "CAD";
  const debouncedCustomerTypeSearch = useDebounce(filterState.customerTypeSearch.trim(), 500);

  useEffect(() => {
    window.sessionStorage.setItem(SESSION_FILTER_DAY_KEY, String(filterDayState));
  }, [filterDayState]);

  useEffect(() => {
    const application = filterState.application?.[0];
    if (application) {
      window.sessionStorage.setItem(SESSION_FILTER_APPLICATION_KEY, application);
    }
  }, [filterState.application]);

  useEffect(() => {
    void loadCustomerTypes();
  }, []);

  useLayoutEffect(() => {
    const pageEl = document.getElementById("check-in-config-page");
    const headerEl = pageEl?.querySelector(".ui-page-header");

    if (!pageEl || !headerEl) {
      return;
    }

    const updateHeaderHeight = () => {
      pageEl.style.setProperty("--check-in-config-header-height", `${headerEl.getBoundingClientRect().height}px`);
    };

    updateHeaderHeight();

    const resizeObserver = new ResizeObserver(updateHeaderHeight);
    resizeObserver.observe(headerEl);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    setVariantFocused({});
    setVariantSearches({});
    void loadVariantTypes();
  }, [filterDayState, filterState.application, filterState.emptyVariant]);

  useEffect(() => {
    void loadCheckIns();
  }, [
    filterDayState,
    filterState.application,
    filterState.emptyVariant,
    filterState.drawerCustomerTypeIds,
    filterState.holes,
    filterState.courses,
    debouncedCustomerTypeSearch,
  ]);

  useEffect(() => {
    setSelectedPricingSheetId(null);
  }, [filterDayState, filterState.application]);

  useEffect(() => {
    if (checkInState.length === 0 || checkInOptionState.length === 0) {
      return;
    }

    const newVariantSearch: Record<number, string> = {};
    checkInState.forEach(checkIn => {
      if (checkIn.variantId) {
        const option = checkInOptionState.find(opt => opt.productSheetId === checkIn.pricingSheetId);
        const variant = option?.variants?.find(v => v.id === checkIn.variantId);
        if (variant) {
          newVariantSearch[checkIn.id] =
            variant.type !== variant.productTitle ? `${variant.productTitle} - ${variant.type}` : variant.productTitle;
        }
      }
    });

    setVariantSearches(prevState => {
      const prevKeys = Object.keys(prevState);
      const nextKeys = Object.keys(newVariantSearch);

      if (
        prevKeys.length === nextKeys.length &&
        nextKeys.every(key => prevState[Number(key)] === newVariantSearch[Number(key)])
      ) {
        return prevState;
      }

      return newVariantSearch;
    });
  }, [checkInState, checkInOptionState]);

  useEffect(() => {
    if (checkInStateBeforeChanges.length === 0 && checkInState.length > 0 && checkInOptionState.length > 0) {
      setCheckInStateBeforeChanges(checkInState);
    }
  }, [checkInState, checkInOptionState, checkInStateBeforeChanges.length]);

  function mapCheckInFromApi(checkInData: any): ICheckIn {
    return {
      id: checkInData.id,
      customerTypeId: checkInData.customer_type_id,
      pricingSheetId: checkInData.pricing_sheet_id,
      variantId: checkInData.variant_id,
      dayOfWeekNumber: Number(checkInData.pricing_sheet.day_of_week_number),
      customerType: checkInData.customer_type?.title,
      customer_type: checkInData.customer_type,
      pricingSheet: checkInData.pricing_sheet.title,
      pricingSheetCourse: checkInData.pricing_sheet.course?.full_name,
      pricingSheetApplication: checkInData.pricing_sheet.application,
      holes: checkInData.holes,
      courseId: checkInData?.pricing_sheet?.course_id,
      teeSheetPriceOverride: checkInData?.tee_sheet_price_override ?? "",
      onlinePriceOverride: checkInData?.online_price_override ?? "",
      pricingSheetCourseId: checkInData?.pricing_sheet?.course_id,
      pricingSheetStartDate: checkInData?.pricing_sheet?.start_date,
      pricingSheetEndDate: checkInData?.pricing_sheet?.end_date,
      variantPrice: checkInData?.variant?.price,
      prepaidPriceOverrideFixed: checkInData?.prepaid_price_override_fixed,
      prepaidPriceOverridePercent: checkInData?.prepaid_price_override_percent,
    };
  }

  async function loadCheckIns() {
    const checkInsResponse = await GetCheckIns(
      {
        day_of_week_number: filterDayState,
        application: filterState.application,
        limit: 100,
        no_variant: filterState.emptyVariant,
        ...(debouncedCustomerTypeSearch ? { search: debouncedCustomerTypeSearch } : {}),
        ...(filterState.drawerCustomerTypeIds?.length ? { customer_type_ids: filterState.drawerCustomerTypeIds } : {}),
        ...(filterState.holes?.length ? { holes: filterState.holes } : {}),
        ...(filterState.courses?.length ? { course_ids: filterState.courses } : {}),
      },
      true,
    );

    if (checkInsResponse.status !== StatusCode.OK) {
      return;
    }

    setCheckInsLoaded(true);
    const checkIns: ICheckIn[] = checkInsResponse.data.map(mapCheckInFromApi);

    ReactDOM.unstable_batchedUpdates(() => {
      setCheckInState(checkIns);
      setCheckInStateBeforeChanges(checkIns);
      setCheckInIds([]);
    });
  }

  async function loadVariantTypes() {
    const currentApplication = filterState.application?.[0];

    const pricingSheetsResponse = await GetPricingSheets(
      {
        day_of_week_number: filterDayState,
        extended: true,
      },
      true,
    );

    if (pricingSheetsResponse.status !== StatusCode.OK) {
      return;
    }

    const dayPricingSheets = parsePricingSheetsResponse(pricingSheetsResponse.data).filter(
      pricingSheet => pricingSheet.day_of_week_number === filterDayState,
    );

    const checkInOptionState: ICheckInOption[] = dayPricingSheets.map(pricingSheet => ({
      productSheetId: pricingSheet.id,
      variants: pricingSheet.variants?.map(mapPricingSheetVariantFromApi) ?? [],
    }));

    setCheckInOptionState(checkInOptionState);
    setPricingSheets(
      dayPricingSheets
        .filter(pricingSheet => !currentApplication || pricingSheet.application === currentApplication)
        .map(mapPricingSheetFromApi),
    );
  }

  async function loadCustomerTypes() {
    const customerTypeRes = await GetCustomerType(null, true);
    if (customerTypeRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting customer types")));
      return;
    }
    setFilterState(prevState => ({ ...prevState, customerTypes: customerTypeRes.data }));
  }

  function handleDropDownChange(value: number, property: string, currentCheckIn: ICheckIn, checkInIndex: number) {
    const currentCheckIns = [...checkInState];

    if (property === undefined) {
      return;
    }

    if (checkInIds.includes(currentCheckIn?.id)) {
      const newCheckIns = currentCheckIns?.map(checkIn => {
        //Get pricing sheet variants for the current check in
        const checkInOption = checkInOptionState?.find(option => option.productSheetId === checkIn?.pricingSheetId);
        if (
          checkInIds.includes(checkIn?.id) &&
          currentCheckIn?.pricingSheetApplication === checkIn?.pricingSheetApplication &&
          checkInOption &&
          checkInOption?.variants?.some(variant => variant?.id === value)
        ) {
          return { ...checkIn, [property]: value };
        } else {
          return checkIn;
        }
      });
      setCheckInState(newCheckIns);
    } else {
      currentCheckIns[checkInIndex] = { ...currentCheckIns[checkInIndex], [property]: value };
      setCheckInState(currentCheckIns);
    }
  }

  function applyOverrideChange(
    currentCheckIn: ICheckIn,
    checkInIndex: number,
    type: "tee_sheet" | "online" | "prepaid",
    value: string,
  ) {
    const currentCheckIns = [...checkInState];
    const id =
      type === "tee_sheet"
        ? "teeSheetPriceOverride"
        : type === "prepaid"
        ? priceOverrideModal?.prepaidType === "percent"
          ? "prepaidPriceOverridePercent"
          : "prepaidPriceOverrideFixed"
        : "onlinePriceOverride";

    if (checkInIds?.includes(currentCheckIn?.id)) {
      const newCheckIns = currentCheckIns?.map(checkIn => {
        if (checkInIds?.includes(checkIn?.id)) {
          return {
            ...checkIn,
            prepaidPriceOverrideFixed:
              priceOverrideModal?.prepaidType === "percent" ? null : checkIn?.prepaidPriceOverrideFixed,
            prepaidPriceOverridePercent:
              priceOverrideModal?.prepaidType === "fixed" ? null : checkIn?.prepaidPriceOverridePercent,
            [id]: value,
          };
        }
        return checkIn;
      });
      setCheckInState(newCheckIns);
      return;
    }

    currentCheckIns[checkInIndex] = {
      ...currentCheckIns[checkInIndex],
      prepaidPriceOverrideFixed:
        priceOverrideModal?.prepaidType === "percent" ? null : currentCheckIns[checkInIndex]?.prepaidPriceOverrideFixed,
      prepaidPriceOverridePercent:
        priceOverrideModal?.prepaidType === "fixed" ? null : currentCheckIns[checkInIndex]?.prepaidPriceOverridePercent,
      [id]: value,
    };
    setCheckInState(currentCheckIns);
  }

  function openOverrideModal(type: "tee_sheet" | "online" | "prepaid", checkIn: ICheckIn, checkInIndex: number) {
    const prepaidPrice = checkIn?.prepaidPriceOverridePercent
      ? checkIn?.prepaidPriceOverridePercent
      : checkIn?.prepaidPriceOverrideFixed;
    const price =
      type === "tee_sheet"
        ? checkIn.teeSheetPriceOverride
        : type === "prepaid"
        ? prepaidPrice
        : checkIn.onlinePriceOverride;
    updatePriceOverrideModal({
      isOpen: true,
      type,
      price: price ?? "",
      currentCheckIn: checkIn,
      checkInIndex,
      prepaidType: checkIn?.prepaidPriceOverridePercent ? "percent" : "fixed",
    });
    setOpenOverrideMenuCheckInId(null);
  }

  function handleOverrideChange() {
    if (!priceOverrideModal?.currentCheckIn || priceOverrideModal.checkInIndex == null) {
      return;
    }

    applyOverrideChange(
      priceOverrideModal.currentCheckIn,
      priceOverrideModal.checkInIndex,
      priceOverrideModal.type,
      priceOverrideModal.price,
    );
    void closeUpdateOverrideModal();
  }

  function handleRemoveOverride() {
    if (!priceOverrideModal?.currentCheckIn || priceOverrideModal.checkInIndex == null) {
      return;
    }

    applyOverrideChange(
      priceOverrideModal.currentCheckIn,
      priceOverrideModal.checkInIndex,
      priceOverrideModal.type,
      "",
    );
    void closeUpdateOverrideModal();
  }

  function checkInHasOverrides(checkIn: ICheckIn): boolean {
    return (
      !!checkIn.teeSheetPriceOverride ||
      !!checkIn.onlinePriceOverride ||
      !!checkIn?.prepaidPriceOverrideFixed ||
      !!checkIn?.prepaidPriceOverridePercent
    );
  }

  function checkInHasOverrideType(checkIn: ICheckIn, type: "tee_sheet" | "online" | "prepaid"): boolean {
    return type === "tee_sheet"
      ? !!checkIn.teeSheetPriceOverride
      : type === "prepaid"
      ? !!checkIn?.prepaidPriceOverrideFixed || !!checkIn?.prepaidPriceOverridePercent
      : !!checkIn.onlinePriceOverride;
  }

  function formatCustomerTypeLabel(customerType?: Pick<ICustomerType, "title" | "subtitle">): string {
    if (!customerType?.title) {
      return "";
    }

    if (!customerType.subtitle) {
      return customerType.title;
    }

    return `${customerType.title} - ${customerType.subtitle}`;
  }

  function getVariants(productSheetId: number): Array<Array<IVariant>> {
    let variants: IVariant[] = [];
    for (let i = 0; i < checkInOptionState.length; i++) {
      if (productSheetId === checkInOptionState[i].productSheetId) {
        variants = checkInOptionState[i].variants;
      }
    }

    return Object.values(groupBy(variants, v => v.productId));
  }

  function getVariantLabel(pricingSheetId: number, variantId: number): string {
    if (!variantId) {
      return "";
    }
    const allVariants = [].concat(...getVariants(pricingSheetId));
    const variant = allVariants.find(v => v.id === variantId);
    if (!variant) {
      return "";
    }
    return variant.type !== variant.productTitle
      ? `${variant.productTitle as string} - ${variant.type as string}`
      : (variant.productTitle as string);
  }

  function formatPricingSheetDateRange(startDate?: string, endDate?: string): string {
    if (!startDate || !endDate) {
      return "";
    }

    return `${moment(startDate, "MM-DD").format("MMM D")} - ${moment(endDate, "MM-DD").format("MMM D")}`;
  }

  function formatPricingSheetTimeRange(startTime?: string, endTime?: string): string {
    if (!startTime || !endTime) {
      return "";
    }

    return `${convertTime(startTime)} - ${convertTime(endTime)}`;
  }

  function formatPricingSheetSupportText(
    sheet: Pick<IPricingSheetOption, "startDate" | "endDate" | "startTime" | "endTime">,
  ): string {
    const parts = [
      formatPricingSheetDateRange(sheet.startDate, sheet.endDate),
      formatPricingSheetTimeRange(sheet.startTime, sheet.endTime),
    ].filter(Boolean);

    return parts.join(" • ");
  }

  function renderPricingSheetSelectValue(option: TSelectOption<TPricingSheetSelectExtra>) {
    if (!option.extraValue?.supportText) {
      return <span>{option.extraValue?.title ?? option.label}</span>;
    }

    return (
      <span className="check-in-config-pricing-sheet-value">
        <span className="check-in-config-pricing-sheet-value__title">{option.extraValue.title}</span>
        <span className="check-in-config-pricing-sheet-value__support">{option.extraValue.supportText}</span>
      </span>
    );
  }

  function renderPricingSheetSelectOption(
    option: TSelectOption<TPricingSheetSelectExtra>,
    selectedValue: number | "all",
  ) {
    const isSelected = option.id === selectedValue;

    return (
      <>
        <div className="check-in-config-pricing-sheet-option">
          <p className="check-in-config-pricing-sheet-option__title">{option.extraValue?.title ?? option.label}</p>
          {option.extraValue?.supportText ? (
            <p className="check-in-config-pricing-sheet-option__support">{option.extraValue.supportText}</p>
          ) : null}
        </div>
        {isSelected ? <FontAwesomeIcon className="ui-select_dropdown_check" icon={["far", "check"]} /> : null}
      </>
    );
  }

  function formatDayAbbrev(day: DayValue): string {
    return DAY_ABBREV[day];
  }

  function formatDayListAbbrev(days: DayValue[]): string {
    return days.map(day => formatDayAbbrev(day)).join(", ");
  }

  function getCopyTargetDays(): DayValue[] {
    return COPY_TARGET_DAYS.filter(day => day !== filterDayState);
  }

  function countDrawerFilters(buffer: IFilterDrawerState): number {
    let count = 0;
    if (buffer.customerTypeIds.length > 0) {
      count++;
    }
    if (buffer.holes.length > 0) {
      count++;
    }
    if (buffer.courses.length > 0) {
      count++;
    }
    if (buffer.emptyVariant) {
      count++;
    }
    return count;
  }

  function countActiveFilters(): number {
    let count = 0;
    if (filterState.drawerCustomerTypeIds?.length) {
      count++;
    }
    if (filterState.holes?.length) {
      count++;
    }
    if (filterState.courses?.length) {
      count++;
    }
    if (filterState.emptyVariant) {
      count++;
    }
    return count;
  }

  function openFiltersDrawer() {
    setFilterDrawerBuffer({
      customerTypeIds: filterState.drawerCustomerTypeIds ?? [],
      holes: filterState.holes ?? [],
      courses: filterState.courses ?? [],
      emptyVariant: filterState.emptyVariant,
      customerTypeSearch: "",
    });
    setShowAllDrawerCustomerTypes(false);
    setFiltersDrawerOpen(true);
  }

  function clearFilterDrawerBuffer() {
    setFilterDrawerBuffer({
      customerTypeIds: [],
      holes: [],
      courses: [],
      emptyVariant: 0,
      customerTypeSearch: "",
    });
  }

  function applyFilterDrawer() {
    setFilterState(prevState => ({
      ...prevState,
      drawerCustomerTypeIds: filterDrawerBuffer.customerTypeIds.length ? filterDrawerBuffer.customerTypeIds : null,
      holes: filterDrawerBuffer.holes.length ? filterDrawerBuffer.holes : null,
      courses: filterDrawerBuffer.courses.length ? filterDrawerBuffer.courses : null,
      emptyVariant: filterDrawerBuffer.emptyVariant,
    }));
    setFiltersDrawerOpen(false);
  }

  function removeCustomerTypeFilter(customerTypeId: number) {
    setFilterState(prevState => {
      const nextIds = (prevState.drawerCustomerTypeIds ?? []).filter(id => id !== customerTypeId);
      return {
        ...prevState,
        drawerCustomerTypeIds: nextIds.length ? nextIds : null,
      };
    });
  }

  function removeHolesFilter(holes: number) {
    setFilterState(prevState => {
      const nextHoles = (prevState.holes ?? []).filter(value => value !== holes);
      return {
        ...prevState,
        holes: nextHoles.length ? nextHoles : null,
      };
    });
  }

  function removeCourseFilter(courseId: number) {
    setFilterState(prevState => {
      const nextCourses = (prevState.courses ?? []).filter(id => id !== courseId);
      return {
        ...prevState,
        courses: nextCourses.length ? nextCourses : null,
      };
    });
  }

  function removeEmptyVariantFilter() {
    setFilterState(prevState => ({
      ...prevState,
      emptyVariant: 0,
    }));
  }

  function removeActiveFilterTag(tag: ActiveFilterTag) {
    switch (tag.type) {
      case "customerType":
        removeCustomerTypeFilter(tag.customerTypeId);
        break;
      case "holes":
        removeHolesFilter(tag.holes);
        break;
      case "course":
        removeCourseFilter(tag.courseId);
        break;
      case "emptyVariant":
        removeEmptyVariantFilter();
        break;
      default:
        break;
    }
  }

  function toggleFilterDrawerCustomerType(customerTypeId: number) {
    setFilterDrawerBuffer(prevState => ({
      ...prevState,
      customerTypeIds: prevState.customerTypeIds.includes(customerTypeId)
        ? prevState.customerTypeIds.filter(id => id !== customerTypeId)
        : [...prevState.customerTypeIds, customerTypeId],
    }));
  }

  function toggleFilterDrawerHoles(holes: number) {
    setFilterDrawerBuffer(prevState => ({
      ...prevState,
      holes: prevState.holes.includes(holes)
        ? prevState.holes.filter(value => value !== holes)
        : [...prevState.holes, holes],
    }));
  }

  async function openCopyModal() {
    setDuplicateState(prevState => ({
      ...prevState,
      showModal: true,
      loading: true,
      warningDismissed: false,
      selectedDays: [],
      selectedCourseId: facilityStore?.facility?.courses?.length > 1 ? facilityStore?.facility?.courses[0]?.id : null,
    }));

    const otherDays = getCopyTargetDays();
    const results = await Promise.all(
      otherDays.map(async day => {
        const response = await GetCheckIns(
          {
            day_of_week_number: day,
            application: filterState.application,
            limit: 100,
            no_variant: 0,
          },
          false,
        );

        if (response.status !== StatusCode.OK) {
          return { day, checkIns: [] as ICheckIn[] };
        }

        return {
          day,
          checkIns: response.data.map(mapCheckInFromApi),
        };
      }),
    );

    const otherDaysCheckIns: Partial<Record<DayValue, ICheckIn[]>> = {};
    results.forEach(({ day, checkIns }) => {
      otherDaysCheckIns[day] = checkIns;
    });

    setDuplicateState(prevState => ({
      ...prevState,
      loading: false,
      otherDaysCheckIns,
    }));
  }

  function closeCopyModal() {
    setDuplicateState(prevState => ({
      ...prevState,
      showModal: false,
      selectedDays: [],
      otherDaysCheckIns: {},
      loading: false,
      warningDismissed: false,
    }));
  }

  function toggleCopyDay(day: DayValue) {
    setDuplicateState(prevState => ({
      ...prevState,
      selectedDays: prevState.selectedDays.includes(day)
        ? prevState.selectedDays.filter(value => value !== day)
        : [...prevState.selectedDays, day],
      warningDismissed: false,
    }));
  }

  const showPricingSheetColumn = selectedPricingSheetId === null;
  const hasActiveTableFilters = countActiveFilters() > 0;
  const showCheckInContent = checkInsLoaded || hasActiveTableFilters;
  const hasTableSearchOrFilters =
    hasActiveTableFilters ||
    !!filterState.customerTypeSearch?.trim() ||
    filterState.drawerCustomerTypeIds?.length ||
    selectedPricingSheetId !== null;

  const dayOptions = useMemo(
    () =>
      Object.entries(DAYS_OF_WEEK).map(([value, label]) => ({
        id: Number(value) as DayValue,
        label: t(label),
      })),
    [t],
  );

  const pricingSheetOptions = useMemo((): TSelectOption<TPricingSheetSelectExtra>[] => {
    const currentApplication = filterState.application?.[0];
    const sheetMap = new Map<number, IPricingSheetOption>();

    pricingSheets.forEach(sheet => {
      if (sheet.dayOfWeekNumber !== filterDayState) {
        return;
      }
      if (sheet.application !== currentApplication) {
        return;
      }
      sheetMap.set(sheet.id, sheet);
    });

    checkInState.forEach(checkIn => {
      if (checkIn.dayOfWeekNumber !== filterDayState) {
        return;
      }
      if (checkIn.pricingSheetApplication !== currentApplication) {
        return;
      }
      if (!sheetMap.has(checkIn.pricingSheetId)) {
        sheetMap.set(checkIn.pricingSheetId, {
          id: checkIn.pricingSheetId,
          title: checkIn.pricingSheet,
          startDate: checkIn.pricingSheetStartDate,
          endDate: checkIn.pricingSheetEndDate,
          application: checkIn.pricingSheetApplication,
          dayOfWeekNumber: checkIn.dayOfWeekNumber,
        });
      }
    });

    const sheets = Array.from(sheetMap.values())
      .sort((a, b) => a.title.localeCompare(b.title))
      .map(sheet => ({
        id: sheet.id,
        label: sheet.title,
        extraValue: {
          title: sheet.title,
          supportText: formatPricingSheetSupportText(sheet),
        },
      }));

    return [
      {
        id: "all" as const,
        label: t("All sheets"),
        extraValue: {
          title: t("All sheets"),
          supportText: "",
        },
      },
      ...sheets,
    ];
  }, [checkInState, filterDayState, filterState.application, pricingSheets, t]);

  const displayedCheckIns = useMemo(() => {
    return checkInState.filter(checkIn => {
      if (checkIn.dayOfWeekNumber !== filterDayState) {
        return false;
      }

      if (selectedPricingSheetId !== null && checkIn.pricingSheetId !== selectedPricingSheetId) {
        return false;
      }

      return true;
    });
  }, [checkInState, filterDayState, selectedPricingSheetId]);

  const displayedCheckInIds = useMemo(() => displayedCheckIns.map(checkIn => checkIn.id), [displayedCheckIns]);

  const allDisplayedCheckInsSelected =
    displayedCheckInIds.length > 0 && displayedCheckInIds.every(id => checkInIds.includes(id));

  const someDisplayedCheckInsSelected =
    displayedCheckInIds.some(id => checkInIds.includes(id)) && !allDisplayedCheckInsSelected;

  function selectAllDisplayedCheckInIds() {
    setCheckInIds(displayedCheckIns.map(checkIn => checkIn.id));
  }

  function handleSelectAllDisplayedCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.checked) {
      selectAllDisplayedCheckInIds();
    } else {
      setCheckInIds([]);
    }
  }

  const OVERRIDE_MENU_MIN_WIDTH = 220;
  const OVERRIDE_MENU_ESTIMATED_HEIGHT = 150;

  const openOverrideMenuTarget = useMemo(() => {
    if (openOverrideMenuCheckInId == null) {
      return null;
    }

    const checkIn = displayedCheckIns.find(row => row.id === openOverrideMenuCheckInId);
    if (!checkIn) {
      return null;
    }

    return {
      checkIn,
      checkInIndex: checkInState.findIndex(row => row.id === checkIn.id),
    };
  }, [openOverrideMenuCheckInId, displayedCheckIns, checkInState]);

  useLayoutEffect(() => {
    if (!openOverrideMenuTarget) {
      setOverrideMenuPosition(null);
      return;
    }

    function updateOverrideMenuPosition() {
      const trigger = overrideTriggerRef.current;
      if (!trigger) {
        return;
      }

      const rect = trigger.getBoundingClientRect();
      const menuHeight = overrideMenuPanelRef.current?.offsetHeight ?? OVERRIDE_MENU_ESTIMATED_HEIGHT;
      const menuWidth = overrideMenuPanelRef.current?.offsetWidth ?? OVERRIDE_MENU_MIN_WIDTH;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openAbove = spaceBelow < menuHeight + 8 && rect.top > menuHeight + 8;
      const top = openAbove ? rect.top - menuHeight - 4 : rect.bottom + 4;
      const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));

      setOverrideMenuPosition({ top, left });
    }

    updateOverrideMenuPosition();
    requestAnimationFrame(updateOverrideMenuPosition);

    window.addEventListener("resize", updateOverrideMenuPosition);
    window.addEventListener("scroll", updateOverrideMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateOverrideMenuPosition);
      window.removeEventListener("scroll", updateOverrideMenuPosition, true);
    };
  }, [openOverrideMenuTarget]);

  useEffect(() => {
    if (openOverrideMenuCheckInId != null && !openOverrideMenuTarget) {
      setOpenOverrideMenuCheckInId(null);
    }
  }, [openOverrideMenuCheckInId, openOverrideMenuTarget]);

  const selectedCheckIns = useMemo(
    () => checkInState.filter(checkIn => checkInIds.includes(checkIn.id)),
    [checkInIds, checkInState],
  );

  const overwriteWarningDays = useMemo(() => {
    if (!duplicateState.showModal || duplicateState.selectedDays.length === 0) {
      return [];
    }

    return duplicateState.selectedDays.filter(day => {
      const dayCheckIns = duplicateState.otherDaysCheckIns[day] ?? [];
      return selectedCheckIns.some(selected =>
        dayCheckIns.some(
          target =>
            target.customerTypeId === selected.customerTypeId && target.holes === selected.holes && !!target.variantId,
        ),
      );
    });
  }, [duplicateState.showModal, duplicateState.selectedDays, duplicateState.otherDaysCheckIns, selectedCheckIns]);

  const drawerCustomerTypes = useMemo(() => {
    const currentApplication = filterState.application?.[0];
    const search = filterDrawerBuffer.customerTypeSearch.trim().toLowerCase();

    return filterState.customerTypes
      .filter(type => type.application === currentApplication)
      .filter(type => !search || type.full_title?.toLowerCase().includes(search));
  }, [filterDrawerBuffer.customerTypeSearch, filterState.application, filterState.customerTypes]);

  const visibleDrawerCustomerTypes = showAllDrawerCustomerTypes
    ? drawerCustomerTypes
    : drawerCustomerTypes.slice(0, FILTER_CUSTOMER_TYPE_LIMIT);

  const drawerFilterCount = countDrawerFilters(filterDrawerBuffer);

  const activeFilterTags = useMemo((): ActiveFilterTag[] => {
    const tags: ActiveFilterTag[] = [];

    filterState.drawerCustomerTypeIds?.forEach(customerTypeId => {
      const customerType = filterState.customerTypes.find(type => type.id === customerTypeId);
      if (customerType) {
        tags.push({
          key: `customer-type-${customerTypeId}`,
          type: "customerType",
          customerTypeId,
          label: formatCustomerTypeLabel(customerType),
        });
      }
    });

    filterState.holes?.forEach(holes => {
      tags.push({
        key: `holes-${holes}`,
        type: "holes",
        holes,
        label: `${holes} ${t("holes")}`,
      });
    });

    filterState.courses?.forEach(courseId => {
      const course = facilityStore?.facility?.courses?.find((item: ICourse) => item.id === courseId);
      tags.push({
        key: `course-${courseId}`,
        type: "course",
        courseId,
        label: course?.long_name ?? String(courseId),
      });
    });

    if (filterState.emptyVariant) {
      tags.push({
        key: "empty-variant",
        type: "emptyVariant",
        label: t("No Variant"),
      });
    }

    return tags;
  }, [
    facilityStore?.facility?.courses,
    filterState.courses,
    filterState.customerTypes,
    filterState.drawerCustomerTypeIds,
    filterState.emptyVariant,
    filterState.holes,
    t,
  ]);

  const tableColumns = useMemo(
    () => [
      {
        label: "",
        width: "48px",
        content: (
          <Checkbox
            id="check-in-config-select-all"
            size="medium"
            labelHidden
            checked={allDisplayedCheckInsSelected}
            indeterminate={someDisplayedCheckInsSelected}
            disabled={displayedCheckInIds.length === 0}
            onChange={handleSelectAllDisplayedCheckboxChange}
          />
        ),
      },
      { label: t("Customer type"), width: "20%" },
      ...(showPricingSheetColumn ? [{ label: t("Pricing Sheet"), width: "18%" }] : []),
      { label: t("Product (base rate)"), width: "28%" },
      {
        label: t("Rate & overrides"),
        width: "22%",
        content: (
          <div className="check-in-config-rate-header">
            <span className="table-label">{t("Rate & overrides")}</span>
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id="check-in-rate-overrides-tooltip" className="check-in-config-rate-header__tooltip">
                  {t("Applies a different price for online or tee sheet bookings.")}
                </Tooltip>
              }
            >
              <span
                className="check-in-config-rate-header__icon-wrap"
                tabIndex={0}
                role="button"
                aria-label={t("Rate & overrides help")}
              >
                <FontAwesomeIcon icon={faCircleQuestion} className="check-in-config-rate-header__icon" />
              </span>
            </OverlayTrigger>
          </div>
        ),
      },
      { label: t("Actions"), width: "64px" },
    ],
    [
      allDisplayedCheckInsSelected,
      displayedCheckInIds.length,
      showPricingSheetColumn,
      someDisplayedCheckInsSelected,
      t,
    ],
  );

  async function saveCheckins() {
    const changedCheckins: IPutCheckIns[] = [];

    for (let i = 0; i < checkInState.length; i++) {
      if (
        !isEqualWith(checkInState[i], checkInStateBeforeChanges[i], (originalValue, newValue) => {
          if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
            return true;
          }
        })
      ) {
        changedCheckins.push({
          id: checkInState[i].id,
          customer_type_id: checkInState[i].customerTypeId,
          pricing_sheet_id: checkInState[i].pricingSheetId,
          variant_id: checkInState[i].variantId,
          holes: checkInState[i].holes,
          tee_sheet_price_override: checkInState[i]?.teeSheetPriceOverride
            ? Number(checkInState[i]?.teeSheetPriceOverride)
            : null,
          online_price_override: checkInState[i]?.onlinePriceOverride
            ? Number(checkInState[i]?.onlinePriceOverride)
            : null,
          prepaid_price_override_fixed: checkInState[i]?.prepaidPriceOverrideFixed
            ? Number(checkInState[i]?.prepaidPriceOverrideFixed)
            : null,
          prepaid_price_override_percent: checkInState[i]?.prepaidPriceOverridePercent
            ? Number(checkInState[i]?.prepaidPriceOverridePercent)
            : null,
        });
      }
    }

    const putCheckInsResponse = await PutCheckIns({ configurations: changedCheckins }, true);

    if (putCheckInsResponse.status !== StatusCode.OK) {
      dispatch(showError(errorMessage(putCheckInsResponse, t("Error saving check in configuration"))));
      return;
    }
    dispatch(showSuccess(t("Successfully saved check in configurations")));
    if (filterState.holes || filterState.courses) {
      setFilterState(prevState => ({ ...prevState, filterInProgress: true }));
    }

    const changedState = checkInState;

    putCheckInsResponse.data.forEach(
      (checkIn: any) => changedState.find(oldCheckIn => oldCheckIn.id === checkIn.id) == checkIn,
    );

    setCheckInState(changedState);
    setCheckInStateBeforeChanges(changedState);
    setCheckInIds([]);
  }

  function unsavedChangesExist() {
    if (checkInStateBeforeChanges.length === 0) {
      return false;
    }

    return !isEqualWith(checkInStateBeforeChanges, checkInState, (originalValue, newValue) => {
      if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
        return true;
      }
    });
  }

  function cancelUnsavedChanges() {
    setCheckInState(checkInStateBeforeChanges);
    setCheckInIds([]);
  }

  function handleCheckboxClick(checkInId: number) {
    const currentCheckInIds = [...checkInIds];
    const foundIndex = currentCheckInIds.indexOf(checkInId);
    if (foundIndex === -1) {
      currentCheckInIds.push(checkInId);
    } else {
      currentCheckInIds.splice(foundIndex, 1);
    }
    setCheckInIds(currentCheckInIds);
  }

  function handleCourseChange(value: number) {
    setDuplicateState(prevState => ({
      ...prevState,
      selectedCourseId: value,
    }));
  }

  async function handleDuplicate() {
    const res = await Promise.all(
      duplicateState.selectedDays.map(day =>
        PutCopyCheckIns(
          {
            check_in_ids: checkInIds,
            course_id: duplicateState?.selectedCourseId ? duplicateState?.selectedCourseId : undefined,
            day_of_week: DAYS_OF_WEEK[day],
          },
          true,
        ),
      ),
    );

    if (res?.some(response => response?.status !== StatusCode.OK)) {
      dispatch(showError(t("Error duplicating check in configs")));
      return;
    }

    const copiedCount = checkInIds.length;
    const copiedDays = formatDayListAbbrev(duplicateState.selectedDays);
    dispatch(showSuccess(t("Copied {{count}} rates to {{days}}.", { count: copiedCount, days: copiedDays })));

    ReactDOM.unstable_batchedUpdates(() => {
      closeCopyModal();
      setCheckInIds([]);
    });
  }

  return (
    <Page
      id="check-in-config-page"
      className="check-in-config-page"
      title={t("Check in configuration")}
      subtitle={t("Assign a rate to each customer type for bookings.")}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: saveCheckins,
        onCancel: cancelUnsavedChanges,
      }}
    >
      <div className="check-in-config-sticky">
        <div className="check-in-config-sticky__tabs">
          <Tabs
            tabs={[
              { content: t("Green Fee") },
              { content: t("Power Cart") },
              { content: t("No Show") },
              { content: t("League Green Fee") },
              { content: t("League Power Cart") },
            ]}
            selected={Math.max(
              0,
              CHECK_IN_APPLICATION_VALUES.findIndex(
                v => filterState.application?.length === 1 && filterState.application[0] === v,
              ),
            )}
            onSelect={index =>
              setFilterState(prev => ({
                ...prev,
                application: [CHECK_IN_APPLICATION_VALUES[index]],
              }))
            }
            variant="underline"
          />
        </div>

        <div className="check-in-config-toolbar check-in-config-sticky__toolbar">
          <div className="check-in-config-toolbar__selectors">
            <div className="check-in-config-toolbar__selector">
              <span className="check-in-config-toolbar__label">{t("Day of week")}:</span>
              <div className="check-in-config-toolbar__day-select-wrap">
                <FontAwesomeIcon icon={faCalendar} className="check-in-config-toolbar__day-icon" />
                <SelectNew<DayValue>
                  id="check-in-day-of-week"
                  labelHidden
                  value={filterDayState}
                  options={dayOptions}
                  onChange={value => setFilterDayState(Number(value) as DayValue)}
                  className="check-in-config-toolbar__select check-in-config-toolbar__day-select"
                />
              </div>
            </div>
            <div className="check-in-config-toolbar__selector">
              <span className="check-in-config-toolbar__label">{t("Pricing Sheet")}:</span>
              <SelectNew<number | "all", TPricingSheetSelectExtra>
                id="check-in-pricing-sheet"
                labelHidden
                value={selectedPricingSheetId ?? "all"}
                options={pricingSheetOptions}
                onChange={value => setSelectedPricingSheetId(value === "all" ? null : Number(value))}
                className="check-in-config-toolbar__select check-in-config-toolbar__pricing-sheet-select"
                renderValue={renderPricingSheetSelectValue}
                renderOption={option => renderPricingSheetSelectOption(option, selectedPricingSheetId ?? "all")}
              />
            </div>
          </div>
        </div>

        {showCheckInContent ? (
          <Card className="check-in-config-sticky__search-card">
            <div className="check-in-config-card-search">
              <div className="check-in-config-card-search__row">
                <div className="check-in-config-toolbar__search-wrap">
                  <Input
                    labelHidden
                    placeholder={t("Search customer type, pricing sheet, rate...")}
                    value={filterState.customerTypeSearch}
                    onChange={e =>
                      setFilterState(prevState => ({
                        ...prevState,
                        customerTypeSearch: e.target.value,
                      }))
                    }
                    onSearchClear={() =>
                      setFilterState(prevState => ({
                        ...prevState,
                        customerTypeSearch: "",
                      }))
                    }
                  />
                </div>
                <Button type="default" onClick={openFiltersDrawer} className="check-in-config-toolbar__filters-button">
                  <FontAwesomeIcon icon={faBarsFilter} className="check-in-config-toolbar__filters-icon" />
                  {t("Filters")}
                  {hasActiveTableFilters ? (
                    <span className="check-in-config-toolbar__filters-count">{countActiveFilters()}</span>
                  ) : null}
                </Button>
              </div>
              {activeFilterTags.length > 0 ? (
                <div className="check-in-config-active-filters">
                  {activeFilterTags.map(tag => (
                    <span key={tag.key} className="check-in-config-active-filters__tag">
                      {tag.label}
                      <button
                        type="button"
                        className="check-in-config-active-filters__remove"
                        aria-label={t("Remove {{filter}} filter", { filter: tag.label })}
                        onClick={() => removeActiveFilterTag(tag)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </Card>
        ) : null}

        {checkInIds.length > 0 ? (
          <div className="check-in-config-bulk-section ">
            <div className="check-in-config-bulk-bar">
              <div className="check-in-config-bulk-bar__left">
                <span className="check-in-config-bulk-bar__count">
                  {checkInIds.length} {t("selected")}
                </span>
                <div className="check-in-config-bulk-bar__links">
                  <button
                    type="button"
                    className="check-in-config-bulk-bar__action"
                    onClick={selectAllDisplayedCheckInIds}
                  >
                    {t("Select all")}
                  </button>
                  <span className="check-in-config-bulk-bar__divider" aria-hidden="true" />
                  <button type="button" className="check-in-config-bulk-bar__action" onClick={() => setCheckInIds([])}>
                    {t("Clear selection")}
                  </button>
                </div>
              </div>
              <Button type="secondary" size="small" onClick={() => void openCopyModal()}>
                {t("Copy to...")}
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      {showCheckInContent ? (
        <>
          <Card>
            <Card.Section table="true">
              {displayedCheckIns.length === 0 && !filterState.filterInProgress ? (
                <div className="check-in-config-empty-state">
                  <MessageDisplay
                    icon="magnifying-glass"
                    title={hasTableSearchOrFilters ? t("No results found") : t("No rates found")}
                    content={
                      hasTableSearchOrFilters
                        ? t("No check in configurations match the current filters.")
                        : t("Create a pricing sheet and assign a rate type to it — they will show up here.")
                    }
                    actions={
                      hasTableSearchOrFilters
                        ? undefined
                        : [
                            {
                              label: `${t("Go to pricing sheets")} →`,
                              action: () => history.push("/admin/settings/tee-sheet/pricing-sheet"),
                              type: "primary",
                            },
                          ]
                    }
                  />
                </div>
              ) : (
                <DataTable columns={tableColumns} loading={filterState.filterInProgress}>
                  {!filterState.filterInProgress
                    ? displayedCheckIns.map((checkIn: ICheckIn) => {
                        const checkInIndex = checkInState.findIndex(row => row.id === checkIn.id);
                        const hasOverrides = checkInHasOverrides(checkIn);
                        const overrideMenuOpen = openOverrideMenuCheckInId === checkIn.id;

                        return (
                          <tr key={checkIn.id}>
                            <td className="table-cell-narrow">
                              <Checkbox
                                onChange={() => handleCheckboxClick(checkIn?.id)}
                                size="medium"
                                checked={checkInIds?.includes(checkIn?.id)}
                              />
                            </td>
                            <td>
                              <div className="check-in-config-customer-type">
                                <span className="check-in-config-customer-type__title">
                                  {formatCustomerTypeLabel(checkIn.customer_type)}
                                </span>
                                <span className="check-in-config-customer-type__holes">
                                  {convertHoles(
                                    checkIn.holes,
                                    facilityStore?.facility?.courses?.find(
                                      (course: ICourse) => course.id === checkIn.pricingSheetCourseId,
                                    ),
                                  )}{" "}
                                  {t("holes")}
                                </span>
                              </div>
                            </td>
                            {showPricingSheetColumn ? (
                              <td>
                                <div className="check-in-config-pricing-sheet">
                                  <span className="check-in-config-pricing-sheet__title">
                                    {facilityStore?.facility?.courses?.length > 1 && (
                                      <span>{checkIn.pricingSheetCourse} - </span>
                                    )}
                                    {checkIn.pricingSheet}
                                  </span>
                                  {formatPricingSheetDateRange(
                                    checkIn.pricingSheetStartDate,
                                    checkIn.pricingSheetEndDate,
                                  ) ? (
                                    <span className="check-in-config-pricing-sheet__dates">
                                      {formatPricingSheetDateRange(
                                        checkIn.pricingSheetStartDate,
                                        checkIn.pricingSheetEndDate,
                                      )}
                                    </span>
                                  ) : null}
                                </div>
                              </td>
                            ) : null}
                            <td className="overflow-visible">
                              <Select
                                key={checkIn.id}
                                onChange={(value: number) => {
                                  handleDropDownChange(value, "variantId", checkIn, checkInIndex);
                                  setVariantSearches(prev => ({ ...prev, [checkIn.id]: "" }));
                                  setVariantFocused(prev => ({ ...prev, [checkIn.id]: false }));
                                }}
                                defaultValue={checkIn.variantId || undefined}
                                placeholder={t("Search and select a product...")}
                                showSearch
                                usePortal
                                showDropDownOnFocus
                                searchValue={
                                  variantFocused[checkIn.id]
                                    ? variantSearches[checkIn.id] ?? ""
                                    : getVariantLabel(checkIn.pricingSheetId, checkIn.variantId)
                                }
                                onSearch={(query: string) => {
                                  if (!variantFocused[checkIn.id] && query === "") {
                                    return;
                                  }

                                  setVariantFocused(prev =>
                                    prev[checkIn.id] ? prev : { ...prev, [checkIn.id]: true },
                                  );
                                  setVariantSearches(prev =>
                                    prev[checkIn.id] === query ? prev : { ...prev, [checkIn.id]: query },
                                  );
                                }}
                                allowClear
                              >
                                {getVariants(checkIn.pricingSheetId)
                                  .map(variantGroup =>
                                    variantGroup.filter(variant => {
                                      const search = variantFocused[checkIn.id]
                                        ? (variantSearches[checkIn.id] ?? "").toLowerCase()
                                        : "";
                                      if (!search) {
                                        return true;
                                      }
                                      const label =
                                        variant.productTitle +
                                        (variant.type !== variant.productTitle ? ` - ${variant.type}` : "");
                                      return label.toLowerCase().includes(search);
                                    }),
                                  )
                                  .filter(variantGroup => variantGroup.length > 0)
                                  .map((variantGroup: IVariant[], index: number) => {
                                    return (
                                      <React.Fragment key={index}>
                                        <p style={{ padding: "8px 16px" }}>
                                          <b>{variantGroup[0].productTitle}</b>
                                        </p>
                                        {variantGroup.map((variant: IVariant, variantIndex: number) => {
                                          return (
                                            <Option key={variantIndex} value={variant.id} name={variant.type}>
                                              <span className="ml-1">
                                                {variant.productTitle}
                                                {variant.type !== variant.productTitle ? (
                                                  <span> - {variant.type}</span>
                                                ) : null}
                                              </span>
                                            </Option>
                                          );
                                        })}
                                      </React.Fragment>
                                    );
                                  })}
                              </Select>
                            </td>
                            <td>
                              <div className="check-in-config-rate">
                                {checkIn.variantPrice != null ? (
                                  <span className="check-in-config-rate__price">
                                    <LocaleCurrency amount={Number(checkIn.variantPrice)} currency={currencyCode} />
                                  </span>
                                ) : null}
                                <div className="check-in-config-rate__overrides">
                                  {hasOverrides ? (
                                    <>
                                      {checkIn.onlinePriceOverride ? (
                                        <span className="check-in-config-rate__override-item">
                                          {t("Online")}:{" "}
                                          <LocaleCurrency
                                            amount={Number(checkIn.onlinePriceOverride)}
                                            currency={currencyCode}
                                          />
                                        </span>
                                      ) : null}
                                      {checkIn.onlinePriceOverride && checkIn.teeSheetPriceOverride ? (
                                        <span className="check-in-config-rate__separator">•</span>
                                      ) : null}
                                      {checkIn.teeSheetPriceOverride ? (
                                        <span className="check-in-config-rate__override-item">
                                          {t("Tee sheet")}:{" "}
                                          <LocaleCurrency
                                            amount={Number(checkIn.teeSheetPriceOverride)}
                                            currency={currencyCode}
                                          />
                                        </span>
                                      ) : null}
                                      {(checkIn.onlinePriceOverride || checkIn.teeSheetPriceOverride) &&
                                      (checkIn.prepaidPriceOverrideFixed || checkIn.prepaidPriceOverridePercent) ? (
                                        <span className="check-in-config-rate__separator">•</span>
                                      ) : null}
                                      {checkIn.prepaidPriceOverrideFixed ? (
                                        <span className="check-in-config-rate__override-item">
                                          {t("Prepaid")}:{" "}
                                          <LocaleCurrency
                                            amount={Number(checkIn.prepaidPriceOverrideFixed)}
                                            currency={currencyCode}
                                          />
                                        </span>
                                      ) : null}
                                      {checkIn.prepaidPriceOverridePercent ? (
                                        <span className="check-in-config-rate__override-item">
                                          {t("Prepaid")}:{" "}
                                          {
                                            <LocaleCurrency
                                              amount={
                                                Number(checkIn?.variantPrice) -
                                                (Number(checkIn?.prepaidPriceOverridePercent) / 100) *
                                                  checkIn?.variantPrice
                                              }
                                              currency={currencyCode}
                                            />
                                          }{" "}
                                          ({checkIn.prepaidPriceOverridePercent}% {t("off")})
                                        </span>
                                      ) : null}
                                    </>
                                  ) : (
                                    <span>{t("No overrides")}</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="check-in-config-actions">
                              <div
                                className="check-in-config-override-trigger"
                                ref={overrideMenuOpen ? overrideTriggerRef : undefined}
                              >
                                <Button
                                  type="text"
                                  size="small"
                                  aria-label={hasOverrides ? t("Edit override") : t("Add override")}
                                  onClick={() => setOpenOverrideMenuCheckInId(overrideMenuOpen ? null : checkIn.id)}
                                >
                                  <FontAwesomeIcon icon={hasOverrides ? faPenToSquare : faPlus} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    : null}
                </DataTable>
              )}
            </Card.Section>
          </Card>
        </>
      ) : null}

      <Drawer
        open={filtersDrawerOpen}
        className="check-in-config-filters-drawer"
        headerLeading={
          <span className="check-in-config-filters-drawer__icon" aria-hidden="true">
            <FontAwesomeIcon icon={faBarsFilter} />
          </span>
        }
        title={t("Filters")}
        subtitle={t("Apply filters to table data")}
        onClose={() => setFiltersDrawerOpen(false)}
        backDropClose
        showCancel={false}
        secondaryActions={[
          { label: t("Clear"), action: clearFilterDrawerBuffer, buttonType: "text" },
          { label: t("Cancel"), action: () => setFiltersDrawerOpen(false), buttonType: "secondary" },
        ]}
        onOk={applyFilterDrawer}
        okText={drawerFilterCount > 0 ? t("Apply {{count}} filter", { count: drawerFilterCount }) : t("Apply")}
      >
        <Drawer.Section bordered>
          <p className="check-in-config-filters-drawer__heading">{t("Customer type")}</p>
          <Input
            placeholder={t("Search customer type")}
            value={filterDrawerBuffer.customerTypeSearch}
            onChange={e =>
              setFilterDrawerBuffer(prevState => ({
                ...prevState,
                customerTypeSearch: e.target.value,
              }))
            }
            className="check-in-config-filters-drawer__search"
          />
          <div className="check-in-config-filters-drawer__options">
            {visibleDrawerCustomerTypes.map(type => (
              <Checkbox
                key={type.id}
                label={type.full_title}
                size="small"
                checked={filterDrawerBuffer.customerTypeIds.includes(type.id)}
                onChange={() => toggleFilterDrawerCustomerType(type.id)}
              />
            ))}
          </div>
          {!showAllDrawerCustomerTypes && drawerCustomerTypes.length > FILTER_CUSTOMER_TYPE_LIMIT ? (
            <button
              type="button"
              className="check-in-config-filters-drawer__show-more"
              onClick={() => setShowAllDrawerCustomerTypes(true)}
            >
              {t("Show {{count}} more", { count: drawerCustomerTypes.length - FILTER_CUSTOMER_TYPE_LIMIT })}
            </button>
          ) : null}
        </Drawer.Section>

        <Drawer.Section bordered>
          <p className="check-in-config-filters-drawer__heading">{t("Holes")}</p>
          <div className="check-in-config-filters-drawer__holes">
            <Checkbox
              label={`9 ${t("holes")}`}
              size="medium"
              selectionOutline
              checked={filterDrawerBuffer.holes.includes(9)}
              onChange={() => toggleFilterDrawerHoles(9)}
            />
            <Checkbox
              label={`18 ${t("holes")}`}
              size="medium"
              selectionOutline
              checked={filterDrawerBuffer.holes.includes(18)}
              onChange={() => toggleFilterDrawerHoles(18)}
            />
          </div>
        </Drawer.Section>

        {facilityStore?.facility?.courses?.length > 1 ? (
          <Drawer.Section bordered>
            <p className="check-in-config-filters-drawer__heading">{t("Courses")}</p>
            <div className="check-in-config-filters-drawer__options">
              {facilityStore.facility.courses.map((course: ICourse) => (
                <Checkbox
                  key={course.id}
                  label={course.long_name}
                  size="small"
                  checked={filterDrawerBuffer.courses.includes(course.id)}
                  onChange={() =>
                    setFilterDrawerBuffer(prevState => ({
                      ...prevState,
                      courses: prevState.courses.includes(course.id)
                        ? prevState.courses.filter(id => id !== course.id)
                        : [...prevState.courses, course.id],
                    }))
                  }
                />
              ))}
            </div>
          </Drawer.Section>
        ) : null}

        <Drawer.Section bordered>
          <p className="check-in-config-filters-drawer__heading">{t("Variant")}</p>
          <Checkbox
            label={t("No Variant")}
            size="small"
            checked={filterDrawerBuffer.emptyVariant === 1}
            onChange={() =>
              setFilterDrawerBuffer(prevState => ({
                ...prevState,
                emptyVariant: prevState.emptyVariant ? 0 : 1,
              }))
            }
          />
        </Drawer.Section>
      </Drawer>

      <Portal isMounted={duplicateState.showModal}>
        <div className="check-in-config-copy-modal-overlay">
          <div className="check-in-config-copy-modal">
            <div className="check-in-config-copy-modal__header">
              <div>
                <h2 className="check-in-config-copy-modal__title">{t("Copy product(s)")}</h2>
                <p className="check-in-config-copy-modal__description">
                  {t("Applies selected products & overrides to the same rows on the day(s) you pick.")}
                </p>
              </div>
              <Button type="close" size="large" onClick={closeCopyModal}>
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>

            <div className="check-in-config-copy-modal__body">
              {facilityStore?.facility?.courses?.length > 1 ? (
                <Select
                  onChange={(value: number) => handleCourseChange(value)}
                  label={t("Course")}
                  defaultValue={facilityStore?.facility?.courses[0]?.id}
                  className="mb-4"
                >
                  {facilityStore?.facility?.courses?.map((course: Record<string, any>, index: number) => (
                    <Option key={index} value={course?.id}>
                      {course?.full_name}
                    </Option>
                  ))}
                </Select>
              ) : null}

              <p className="check-in-config-copy-modal__label">{t("Day of week")}:</p>
              <div className="check-in-config-copy-modal__days">
                {getCopyTargetDays().map(day => (
                  <Chip
                    key={day}
                    className="check-in-config-copy-modal__day-chip"
                    label={t(DAYS_OF_WEEK[day])}
                    size="md"
                    selected={duplicateState.selectedDays.includes(day)}
                    onClick={() => toggleCopyDay(day)}
                  />
                ))}
              </div>

              {!duplicateState.warningDismissed && overwriteWarningDays.length > 0 ? (
                <div className="check-in-config-copy-modal__warning">
                  <Callout
                    type="info"
                    title=""
                    content={
                      <span>
                        <strong>{overwriteWarningDays.map(day => formatDayAbbrev(day)).join(" & ")}</strong>{" "}
                        {t("already have products set. Copying will")} <strong>{t("overwrite")}</strong> {t("them.")}
                      </span>
                    }
                    onClose={() => setDuplicateState(prevState => ({ ...prevState, warningDismissed: true }))}
                  />
                </div>
              ) : null}
            </div>

            <div className="check-in-config-copy-modal__footer">
              <Button type="default" onClick={closeCopyModal}>
                {t("Cancel")}
              </Button>
              <Button
                type="primary"
                onClick={() => void handleDuplicate()}
                disabled={duplicateState.selectedDays.length === 0 || duplicateState.loading}
                loading={duplicateState.loading}
              >
                {duplicateState.selectedDays.length > 0
                  ? t("Copy to {{count}} days", { count: duplicateState.selectedDays.length })
                  : t("Copy to days")}
              </Button>
            </div>
          </div>
          <div className="check-in-config-copy-modal-backdrop" onClick={closeCopyModal} />
        </div>
      </Portal>

      <Portal isMounted={priceOverrideModal.isOpen}>
        <div className="check-in-config-override-modal-overlay">
          <div className="check-in-config-override-modal">
            <div className="check-in-config-override-modal__header">
              <div>
                <h2 className="check-in-config-override-modal__title">
                  {priceOverrideModal?.type === "tee_sheet"
                    ? t("Tee sheet override rate")
                    : priceOverrideModal?.type === "prepaid"
                    ? t("Prepaid override rate")
                    : t("Online override rate")}
                </h2>
                <p className="check-in-config-override-modal__description">
                  {priceOverrideModal?.type === "tee_sheet"
                    ? t("Charge a different rate for bookings made through the tee sheet.")
                    : priceOverrideModal?.type === "prepaid"
                    ? t("Charge a different rate for prepaid bookings")
                    : t("Charge a different rate for bookings made online.")}
                </p>
              </div>
              <Button type="close" size="large" onClick={closeUpdateOverrideModal}>
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>

            <div className="check-in-config-override-modal__body">
              <FormLayout>
                {priceOverrideModal?.type === "prepaid" ? (
                  <FormLayout.Group>
                    <Checkbox
                      selectionOutline
                      size="medium"
                      label={t("Fixed")}
                      checked={priceOverrideModal?.prepaidType === "fixed"}
                      onChange={() => updatePriceOverrideModal({ prepaidType: "fixed" })}
                    />
                    <Checkbox
                      selectionOutline
                      size="medium"
                      label={t("Percent")}
                      checked={priceOverrideModal?.prepaidType === "percent"}
                      onChange={() => updatePriceOverrideModal({ prepaidType: "percent" })}
                    />
                  </FormLayout.Group>
                ) : null}
                <FormLayout.Group>
                  <Input
                    label={
                      priceOverrideModal?.type === "tee_sheet"
                        ? t("Tee sheet rate")
                        : priceOverrideModal?.type === "prepaid"
                        ? t("Prepaid rate")
                        : t("Online rate")
                    }
                    prefix={
                      priceOverrideModal?.type === "prepaid" && priceOverrideModal?.prepaidType === "percent"
                        ? null
                        : getCurrencySymbol(currencyCode)
                    }
                    suffix={
                      priceOverrideModal?.type === "prepaid" && priceOverrideModal?.prepaidType === "percent"
                        ? "%"
                        : null
                    }
                    type="number"
                    placeholder={
                      priceOverrideModal?.type === "prepaid" && priceOverrideModal?.prepaidType === "percent"
                        ? t("e.g., {{amount}}", { amount: `5%` })
                        : t("e.g., {{amount}}", { amount: `${getCurrencySymbol(currencyCode)}50` })
                    }
                    value={priceOverrideModal?.price ?? ""}
                    onChange={e => updatePriceOverrideModal({ price: e.target.value })}
                    helpText={
                      priceOverrideModal?.currentCheckIn?.variantPrice != null &&
                      priceOverrideModal?.type === "prepaid" &&
                      priceOverrideModal?.prepaidType === "percent" ? (
                        checkInIds?.length > 1 ? (
                          t("{{percentOff}}% off the base rate of the selected variants", {
                            percentOff: priceOverrideModal?.price,
                          })
                        ) : (
                          t(
                            "{{percentOff}}% off the base rate of {{currencySymbol}}{{baseRate}}: {{currencySymbol}}{{finalPrice}}",
                            {
                              percentOff: priceOverrideModal?.price,
                              baseRate: priceOverrideModal.currentCheckIn.variantPrice,
                              currencySymbol: getCurrencySymbol(currencyCode),
                              finalPrice:
                                Number(priceOverrideModal.currentCheckIn.variantPrice) -
                                (Number(priceOverrideModal?.price) / 100) *
                                  priceOverrideModal.currentCheckIn.variantPrice,
                            },
                          )
                        )
                      ) : priceOverrideModal?.currentCheckIn?.variantPrice != null ? (
                        <span>
                          {t("Base rate")}:{" "}
                          <LocaleCurrency
                            amount={Number(priceOverrideModal.currentCheckIn.variantPrice)}
                            currency={currencyCode}
                          />
                        </span>
                      ) : undefined
                    }
                  />
                </FormLayout.Group>
              </FormLayout>
            </div>

            <div className="check-in-config-override-modal__footer">
              {priceOverrideModal?.currentCheckIn &&
              checkInHasOverrideType(priceOverrideModal.currentCheckIn, priceOverrideModal.type) ? (
                <Button type="text" onClick={handleRemoveOverride}>
                  {t("Remove override")}
                </Button>
              ) : (
                <span />
              )}
              <div className="check-in-config-override-modal__footer-actions">
                <Button type="default" onClick={closeUpdateOverrideModal}>
                  {t("Cancel")}
                </Button>
                <Button type="primary" onClick={handleOverrideChange} disabled={!priceOverrideModal?.price}>
                  {t("Save")}
                </Button>
              </div>
            </div>
          </div>
          <div className="check-in-config-override-modal-backdrop" onClick={closeUpdateOverrideModal} />
        </div>
      </Portal>

      <Portal isMounted={openOverrideMenuTarget != null}>
        <div
          ref={overrideMenuPanelRef}
          className="check-in-config-override-menu check-in-config-override-menu--portal"
          style={
            overrideMenuPosition
              ? {
                  top: overrideMenuPosition.top,
                  left: overrideMenuPosition.left,
                }
              : { visibility: "hidden" }
          }
        >
          <p className="check-in-config-override-menu__header">{t("Override rate for...")}</p>
          <button
            type="button"
            className="check-in-config-override-menu__option"
            onClick={() =>
              openOverrideModal("tee_sheet", openOverrideMenuTarget.checkIn, openOverrideMenuTarget.checkInIndex)
            }
          >
            {t("Tee sheet bookings")}
          </button>
          <button
            type="button"
            className="check-in-config-override-menu__option"
            onClick={() =>
              openOverrideModal("online", openOverrideMenuTarget.checkIn, openOverrideMenuTarget.checkInIndex)
            }
          >
            {t("Online bookings")}
          </button>
          {openOverrideMenuTarget?.checkIn?.pricingSheetApplication === "green_fee" ? (
            <button
              type="button"
              className="check-in-config-override-menu__option"
              onClick={() =>
                openOverrideModal("prepaid", openOverrideMenuTarget.checkIn, openOverrideMenuTarget.checkInIndex)
              }
            >
              {t("Prepaid bookings")}
            </button>
          ) : null}
        </div>
      </Portal>
    </Page>
  );
}
